import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Avatar } from "../../components/AvatarBuilder";
import { METRIC_META } from "../../components/MetricsViz";
import { AwardCard } from "../../components/Awards";
import { ChallengeAggregations } from "../../components/ChallengeAggregations";

/**
 * Printable session report for the teacher.
 * Triggers window.print() for a clean PDF via browser, or sends an HTML
 * summary by e-mail via Resend (sender domain digitalnimost.cz).
 */
export default function SessionReport() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [workshop, setWorkshop] = useState(null);
  const [report, setReport] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [perChallenge, setPerChallenge] = useState({});
  const [awards, setAwards] = useState([]);
  const [showEmail, setShowEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    try {
      const t = JSON.parse(localStorage.getItem("dm_teacher") || "null");
      if (t?.email) setEmailRecipient(t.email);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, p, aw] = await Promise.all([
          api.get(`/sessions/${sessionId}`),
          api.get(`/sessions/${sessionId}/report`),
          api.get(`/sessions/${sessionId}/participants`),
          api.get(`/sessions/${sessionId}/awards`),
        ]);
        setSession(s.data);
        setReport(r.data);
        setParticipants(p.data);
        setAwards(aw.data.awards || []);
        const w = await api.get(`/workshops/${s.data.workshop_id}`);
        setWorkshop(w.data);
        const results = {};
        for (const ch of w.data.challenges) {
          try {
            const { data } = await api.get(`/sessions/${sessionId}/results/${ch.id}`);
            results[ch.id] = data;
          } catch {
            /* skip */
          }
        }
        setPerChallenge(results);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [sessionId]);

  const sendEmail = async () => {
    if (!emailRecipient.trim()) return toast.error("Zadej e-mail");
    setSending(true);
    try {
      await api.post(`/sessions/${sessionId}/email-report`, {
        recipient: emailRecipient.trim(),
      });
      toast.success(`Report odeslán na ${emailRecipient.trim()}`);
      setShowEmail(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "E-mail se nepodařilo odeslat");
    } finally {
      setSending(false);
    }
  };

  if (!session || !report || !workshop) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Načítám report…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 print:bg-white print:py-0">
      {/* No-print toolbar */}
      <div className="max-w-4xl mx-auto px-8 print:hidden flex items-center justify-between mb-6">
        <Link to={`/teacher/session/${sessionId}`} className="text-sm font-bold text-slate-500 hover:text-slate-900">
          ← Zpět na session
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md px-4 py-2.5 text-sm"
            data-testid="btn-print-report"
          >
            🖨️ Stáhnout jako PDF
          </button>
          <button
            onClick={() => setShowEmail(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md px-4 py-2.5 text-sm"
            data-testid="btn-open-email-report"
          >
            ✉️ Poslat e-mailem
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto bg-white print:bg-white border border-slate-200 print:border-0 rounded-lg p-10 print:p-0 print:max-w-none shadow-sm print:shadow-none">
        {/* Header */}
        <div className="border-b-2 border-slate-900 pb-6 mb-8">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500">Report ze session</div>
          <h1 className="text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">{session.title}</h1>
          <div className="text-sm text-slate-500 mt-2">
            {session.workshop_title} · Kód <span className="font-mono font-bold">{session.code}</span> ·{" "}
            {new Date(session.created_at).toLocaleString("cs-CZ")}
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Účastníků" value={participants.length} />
          <Stat label="Dokončilo avatara" value={participants.filter((p) => p.avatar_config).length} />
          <Stat label="Challenge odehráno" value={Object.keys(perChallenge).filter((k) => perChallenge[k]?.total_responses > 0).length} />
          <Stat label="Status" value={session.status === "finished" ? "Ukončeno" : "Aktivní"} />
        </div>

        {/* Awards ceremony */}
        {awards.length > 0 && (
          <section className="mb-8 break-inside-avoid">
            <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">🏆 Ocenění třídy</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {awards.map((a) => (
                <AwardCard key={a.id} award={a} compact />
              ))}
            </div>
          </section>
        )}

        {/* Average metrics */}
        <section className="mb-8">
          <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">Průměrné metriky třídy</h2>
          <div className="grid grid-cols-5 gap-3">
            {Object.entries(METRIC_META).map(([k, m]) => (
              <div key={k} className="border border-slate-200 rounded-md p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{m.label}</div>
                <div className="text-3xl font-heading font-black tabular-nums mt-1" style={{ color: m.color }}>
                  {report.average[k]}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Endings distribution */}
        <section className="mb-8">
          <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">Typy zakončení</h2>
          <div className="space-y-2">
            {Object.entries(report.ending_distribution).map(([ending, count]) => {
              const pct = participants.length ? (count / participants.length) * 100 : 0;
              return (
                <div key={ending}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-bold text-slate-900">{ENDING_LABELS[ending] || ending}</span>
                    <span className="tabular-nums text-slate-500">{count} žáků</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Per-challenge breakdown */}
        <section className="mb-8">
          <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">Výsledky jednotlivých výzev</h2>
          <div className="space-y-5">
            {workshop.challenges.map((ch, i) => {
              const res = perChallenge[ch.id];
              if (!res || res.total_responses === 0) return null;
              const publicNote = session.lecturer_notes_custom?.[ch.id]?.public;
              return (
                <div key={ch.id} className="border-l-4 border-slate-900 pl-4 py-1 break-inside-avoid">
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    Výzva #{i + 1} · {res.total_responses} / {res.participant_count} odpovědělo
                  </div>
                  <div className="font-bold text-slate-900">{ch.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{ch.context}</div>

                  {res.total_responses > 0 && (
                    <div className="mt-3">
                      <ChallengeAggregations challenge={ch} results={res} responded={res.total_responses} />
                    </div>
                  )}

                  {publicNote && (
                    <div className="mt-3 bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r text-sm text-slate-800">
                      <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">Poznámka lektora</div>
                      {publicNote}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Participant list */}
        <section className="mb-8 break-inside-avoid">
          <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">Účastníci</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {report.participants.map((p) => (
              <div key={p.id} className="flex items-center gap-2 border border-slate-200 rounded-md p-2">
                {p.avatar_config ? (
                  <div style={{ background: `#${p.avatar_config.bg}` }} className="w-8 h-8 rounded-md overflow-hidden border border-slate-300">
                    <Avatar config={p.avatar_config} size={32} />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-md bg-slate-200" />
                )}
                <div className="text-sm font-bold truncate">{p.nickname}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Reflexe pro lektora */}
        <section className="border-t border-slate-200 pt-6 break-inside-avoid">
          <h2 className="text-xl font-heading font-black tracking-tight text-slate-900 mb-3">Doporučené otázky k debriefu</h2>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            <li>Co bylo ve třídě nejjednodušší / nejtěžší rozhodnutí a proč?</li>
            <li>Kde jste se rozhodovali intuitivně a kde analyticky?</li>
            <li>Co z dnešního workshopu můžete použít ještě tento týden?</li>
            <li>Pokud něco podobného zažijete v realitě, na koho se obrátíte?</li>
          </ul>
        </section>

        <div className="mt-10 text-xs text-slate-400 text-center">
          digitální most · report vygenerován {new Date().toLocaleString("cs-CZ")}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {showEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:hidden" data-testid="email-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <div>
              <h3 className="font-heading font-black text-xl text-slate-900">Poslat report e-mailem</h3>
              <p className="text-xs text-slate-500 mt-1">
                Pošleme HTML shrnutí z domény digitalnimost.cz. Detailní PDF získáš tlačítkem „Stáhnout jako PDF".
              </p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Příjemce</label>
              <input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="ucitel@skola.cz"
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="input-email-recipient"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowEmail(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 font-bold"
                disabled={sending}
              >
                Zrušit
              </button>
              <button
                onClick={sendEmail}
                disabled={sending || !emailRecipient}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold rounded-md px-4 py-2 text-sm"
                data-testid="btn-send-email-report"
              >
                {sending ? "Odesílám…" : "Odeslat"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-4">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-3xl font-heading font-black tracking-tight text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

const ENDING_LABELS = {
  connected: "Propojený a silný",
  resilient: "Odolný a rozvážný",
  overwhelmed: "Přetížený",
  isolated: "Izolovaný",
  balanced: "Hledá rovnováhu",
};
